import React from 'react';
import { KeyRound, RotateCcw, Plus, X } from 'lucide-react';
import { RESERVED_DOMAINS, RESERVED_DOMAIN_BY_KEY } from './reservedDomains';
import { plural, useStableKeys } from './governance';
import { MentionInput, OrgMentionsContext, plainMentions, structureMentionItems } from './structure/focus';

const asText = (value) => String(value ?? '').trim();

// Élément de liste sur une ou plusieurs lignes selon sa longueur (une redevabilité longue reste lisible en entier) ;
// Entrée ne crée pas de saut de ligne.
const GrowingInput = ({ value, onChange, placeholder, disabled, ariaLabel }) => {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\s*\n\s*/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
      }}
      className="flex-1 resize-none overflow-hidden bg-transparent border-none outline-none text-sm leading-5 px-2 py-1 text-slate-700 placeholder-slate-400"
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
};
const asList = (value) => (Array.isArray(value) ? value : []);

export const SmartDiffInput = ({
  label,
  originalValue,
  currentValue,
  onChange,
  multiline = false,
  placeholder = '',
  className = '',
  inputClassName = '',
  hideLabel = false,
  disabled = false,
}) => {
  const hasBaseline = originalValue !== undefined && originalValue !== null;
  const normOriginal = asText(originalValue);
  const normCurrent = asText(currentValue);
  const isModified = hasBaseline && normOriginal !== normCurrent;

  const baseInputClass = isModified
    ? 'border-amber-300 bg-amber-50/50'
    : 'border-slate-200 bg-white';

  const defaultClasses = `w-full border rounded-lg px-3 py-2 text-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-100 outline-none ${baseInputClass}`;

  const roleCardHighlight = isModified ? 'bg-amber-50/50 ring-1 ring-amber-200 rounded px-1 -mx-1' : '';

  const effectiveInputClass = `${inputClassName ? `${inputClassName} ${roleCardHighlight}` : defaultClasses} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {!hideLabel && (
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
          {isModified && (
            <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-100 font-medium">
              Modifié
            </span>
          )}
        </div>
      )}

      {isModified && normOriginal && (
        <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-2 text-xs text-slate-600 mb-1">
          <span className="font-semibold text-amber-700 mr-1 uppercase text-[10px]">Avant :</span>
          {normOriginal}
        </div>
      )}

      {multiline ? (
        <textarea
          value={currentValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={effectiveInputClass}
          rows={3}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={hideLabel ? label : undefined}
        />
      ) : (
        <input
          value={currentValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={effectiveInputClass}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={hideLabel ? label : undefined}
        />
      )}
    </div>
  );
};

export const SmartDiffList = ({
  label,
  originalList = [],
  currentList = [],
  onChange,
  disabled = false,
  prepend = null,
  addon = null,
  extraAdded = 0,
  extraRemoved = 0,
  hint = null,
}) => {
  const hasBaseline = Array.isArray(originalList);
  const items = asList(currentList);

  const originalSet = new Set(asList(originalList).map(asText).filter(Boolean));
  const currentSet = new Set(items.map(asText).filter(Boolean));

  const addedCount = (hasBaseline ? items.filter((item) => asText(item) && !originalSet.has(asText(item))).length : 0) + extraAdded;
  const removedItems = hasBaseline
    ? asList(originalList).filter((item) => asText(item) && !currentSet.has(asText(item)))
    : [];
  const removedCount = removedItems.length + extraRemoved;
  const stable = useStableKeys(items.length);
  // « @ » cite un cercle, un rôle ou une règle de l'organisation (ex. « Décision réservée »).
  const mentionItems = structureMentionItems(React.useContext(OrgMentionsContext).mentionItems);

  const handleItemChange = (index, newValue) => {
    const newList = [...items];
    newList[index] = newValue;
    onChange(newList);
  };

  const handleRemove = (index) => {
    if (disabled) return;
    stable.remove(index);
    onChange(items.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    if (disabled) return;
    stable.append();
    onChange([...items, '']);
  };

  const handleRestore = (itemValue) => {
    if (disabled) return;
    stable.append();
    onChange([...items, itemValue]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
        {hasBaseline && (
          <div className="flex gap-2 text-[10px]">
            {addedCount > 0 && (
              <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                +{plural(addedCount, 'ajout')}
              </span>
            )}
            {removedCount > 0 && (
              <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
                −{plural(removedCount, 'retrait')}
              </span>
            )}
          </div>
        )}
      </div>
      {hint && <p className="text-[11px] text-slate-400 -mt-1">{hint}</p>}

      <div className="bg-white border border-slate-200 rounded-xl p-2 shadow-sm space-y-2">
        {prepend}
        {hasBaseline && removedItems.length > 0 && (
          <div className="space-y-1 pb-2 border-b border-slate-100 mb-2">
            <p className="text-[10px] text-slate-400 font-semibold uppercase px-1">Retirés</p>
            {removedItems.map((item, idx) => (
              <div key={`removed-${idx}-${asText(item)}`} className="flex items-center justify-between group px-2 py-1.5 bg-red-50/50 rounded-lg border border-red-100/50">
                <span className="text-sm text-red-400 line-through decoration-red-300">{plainMentions(item)}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRestore(item)}
                    className="text-emerald-600 hover:text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 p-1 rounded-md transition-all shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100"
                    title="Remettre"
                    aria-label={`Remettre « ${asText(item)} »`}
                  >
                    <RotateCcw size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {items.length === 0 && !prepend && (
            <p className="text-xs text-slate-400 italic px-1">Aucun élément.</p>
          )}
          {items.map((item, idx) => {
            const isNew = hasBaseline && asText(item) !== '' && !originalSet.has(asText(item));
            return (
              <div key={stable.keys[idx]} className={`flex items-center gap-2 transition-all duration-300 ${isNew ? 'pl-1' : ''}`}>
                {isNew && <div className="w-1 h-8 bg-emerald-400 rounded-full"></div>}
                <div className={`flex-1 flex items-center gap-2 border rounded-lg px-1 py-1 focus-within:ring-2 focus-within:ring-indigo-100 transition-all ${isNew ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}>
                  {mentionItems.length > 0 ? (
                    <div className="min-w-0 flex-1">
                      <MentionInput
                        multiline
                        value={item ?? ''}
                        onChange={(value) => handleItemChange(idx, value)}
                        mentionItems={mentionItems}
                        placeholder="Nouvel élément… (@ cite un rôle ou une règle)"
                        disabled={disabled}
                        ariaLabel={`${label} ${idx + 1}`}
                      />
                    </div>
                  ) : (
                    <GrowingInput
                      value={item ?? ''}
                      onChange={(value) => handleItemChange(idx, value)}
                      placeholder="Nouvel élément…"
                      disabled={disabled}
                      ariaLabel={`${label} ${idx + 1}`}
                    />
                  )}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="text-slate-300 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                      aria-label="Retirer"
                      title="Retirer"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!disabled && (
          <div className="pt-1 flex flex-wrap items-center justify-end gap-2">
            {addon}
            <button
              type="button"
              onClick={handleAdd}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Une ligne de domaine à effet dans la liste des domaines : libellé, ce qu'il réserve, retrait ou remise.
const EffectDomainRow = ({ domain, state = null, disabled, onRemove, onRestore }) => (
  <div
    className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 ${
      state === 'removed'
        ? 'border-red-100 bg-red-50/50'
        : state === 'added'
          ? 'border-emerald-200 bg-emerald-50/40'
          : 'border-violet-100 bg-violet-50/50'
    }`}
  >
    <KeyRound size={13} className={`mt-0.5 shrink-0 ${state === 'removed' ? 'text-red-300' : 'text-violet-500'}`} />
    <span className="min-w-0 flex-1">
      <span className={`flex items-center gap-1.5 text-sm font-medium ${state === 'removed' ? 'text-red-400 line-through' : 'text-slate-800'}`}>
        {domain.label}
        {state === 'added' && <span className="text-[10px] font-semibold text-emerald-700 no-underline">ajouté</span>}
      </span>
      <span className="block text-[11px] leading-snug text-slate-500">{domain.effect}</span>
    </span>
    {!disabled && state === 'removed' && (
      <button
        type="button"
        onClick={onRestore}
        className="text-emerald-600 hover:text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 p-1 rounded-md"
        title="Remettre"
        aria-label={`Remettre « ${domain.label} »`}
      >
        <RotateCcw size={12} />
      </button>
    )}
    {!disabled && state !== 'removed' && (
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-300 hover:text-red-500 p-1 rounded-md hover:bg-red-50"
        title="Retirer"
        aria-label={`Retirer « ${domain.label} »`}
      >
        <X size={14} />
      </button>
    )}
  </div>
);

/**
 * Domaines d'un rôle, en une seule liste : les domaines écrits librement et ceux « à effet » (🔑), qui réservent en
 * plus un droit dans Deepflow aux titulaires du rôle. Les deux restent deux champs (domains, reserved_domains).
 */
export const DomainsField = ({
  originalDomains,
  currentDomains = [],
  onChangeDomains,
  originalKeys,
  currentKeys = [],
  onChangeKeys,
  disabled = false,
  label = 'Domaines',
}) => {
  const hasBaseline = Array.isArray(originalKeys);
  const keys = asList(currentKeys).filter((key) => RESERVED_DOMAIN_BY_KEY[key]);
  const before = asList(originalKeys).filter((key) => RESERVED_DOMAIN_BY_KEY[key]);
  const removedKeys = hasBaseline ? before.filter((key) => !keys.includes(key)) : [];
  const addedKeys = hasBaseline ? keys.filter((key) => !before.includes(key)) : [];
  const available = RESERVED_DOMAINS.filter((domain) => !keys.includes(domain.key));
  // Ordre stable : celui de la liste de référence.
  const setKeys = (next) => onChangeKeys(RESERVED_DOMAINS.map((d) => d.key).filter((k) => next.includes(k)));

  const prepend = keys.length > 0 || removedKeys.length > 0 ? (
    <div className="space-y-1.5">
      {keys.map((key) => (
        <EffectDomainRow
          key={key}
          domain={RESERVED_DOMAIN_BY_KEY[key]}
          state={addedKeys.includes(key) ? 'added' : null}
          disabled={disabled}
          onRemove={() => setKeys(keys.filter((k) => k !== key))}
        />
      ))}
      {removedKeys.map((key) => (
        <EffectDomainRow
          key={`removed-${key}`}
          domain={RESERVED_DOMAIN_BY_KEY[key]}
          state="removed"
          disabled={disabled}
          onRestore={() => setKeys([...keys, key])}
        />
      ))}
    </div>
  ) : null;

  const addon = available.length > 0 ? (
    <select
      value=""
      onChange={(event) => event.target.value && setKeys([...keys, event.target.value])}
      className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100"
      aria-label="Ajouter un domaine à effet"
      title="Un domaine à effet réserve un droit dans Deepflow aux titulaires du rôle"
    >
      <option value="">+ Domaine à effet…</option>
      {available.map((domain) => (
        <option key={domain.key} value={domain.key}>{domain.label}</option>
      ))}
    </select>
  ) : null;

  return (
    <SmartDiffList
      label={label}
      originalList={Array.isArray(originalDomains) ? originalDomains : null}
      currentList={currentDomains}
      onChange={onChangeDomains}
      disabled={disabled}
      prepend={prepend}
      addon={addon}
      extraAdded={addedKeys.length}
      extraRemoved={removedKeys.length}
    />
  );
};

const SmartDiffFields = { SmartDiffInput, SmartDiffList, DomainsField };

export default SmartDiffFields;
